import { createTheme, alpha } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';

/**
 * Menusso Admin — Modern theme
 * Neutral base + a single blue accent, soft depth, rounded surfaces,
 * clear type hierarchy, visible focus rings.
 */
export const BRAND_PRIMARY = '#1b8fff';

export const theme = createTheme({
  typography: {
    fontFamily: 'var(--font-stack)',
    h1: {
      fontSize: 'var(--text-4xl)',
      fontWeight: 800,
      letterSpacing: 'var(--tracking-tight)',
      lineHeight: 'var(--leading-tight)',
    },
    h2: {
      fontSize: 'var(--text-3xl)',
      fontWeight: 800,
      letterSpacing: 'var(--tracking-tight)',
      lineHeight: 'var(--leading-tight)',
    },
    h3: {
      fontSize: 'var(--text-2xl)',
      fontWeight: 700,
      letterSpacing: 'var(--tracking-tight)',
      lineHeight: 'var(--leading-snug)',
    },
    h4: {
      fontSize: 'var(--text-xl)',
      fontWeight: 700,
      letterSpacing: 'var(--tracking-tight)',
      lineHeight: 'var(--leading-snug)',
    },
    h5: { fontSize: 'var(--text-lg)', fontWeight: 700, lineHeight: 'var(--leading-snug)' },
    h6: { fontSize: 'var(--text-md)', fontWeight: 700, lineHeight: 'var(--leading-snug)' },
    subtitle1: { fontSize: 'var(--text-md)', fontWeight: 600, lineHeight: 'var(--leading-normal)' },
    subtitle2: { fontSize: 'var(--text-sm)', fontWeight: 600, lineHeight: 'var(--leading-normal)' },
    body1: { fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)' },
    body2: { fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)' },
    caption: {
      fontSize: 'var(--text-xs)',
      lineHeight: 'var(--leading-normal)',
      letterSpacing: 'var(--tracking-wide)',
    },
    button: { textTransform: 'none', fontWeight: 600, fontSize: 'var(--text-base)' },
    overline: {
      fontSize: 'var(--text-2xs)',
      fontWeight: 700,
      letterSpacing: 'var(--tracking-caps)',
      textTransform: 'uppercase',
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#1b8fff',
      light: '#4da6ff',
      dark: '#006ddb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1b8fff',
      contrastText: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
      disabled: '#94a3b8',
    },
    background: { default: '#f7f8fa', paper: '#ffffff' },
    divider: '#eceef2',
    success: { main: '#16a34a' },
    warning: { main: '#d97706' },
    error: { main: '#dc2626' },
    action: {
      hover: 'rgba(15, 23, 42, 0.04)',
      selected: 'rgba(27, 143, 255, 0.10)',
    },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px rgba(15,23,42,0.04)',
    '0 1px 3px rgba(15,23,42,0.06),0 1px 2px rgba(15,23,42,0.04)',
    '0 4px 12px rgba(15,23,42,0.06),0 2px 4px rgba(15,23,42,0.04)',
    '0 6px 16px rgba(15,23,42,0.07),0 2px 6px rgba(15,23,42,0.04)',
    '0 8px 20px rgba(15,23,42,0.08),0 3px 8px rgba(15,23,42,0.04)',
    '0 12px 32px rgba(15,23,42,0.08),0 4px 8px rgba(15,23,42,0.04)',
    '0 16px 40px rgba(15,23,42,0.10),0 4px 10px rgba(15,23,42,0.04)',
    '0 20px 48px rgba(15,23,42,0.12),0 6px 12px rgba(15,23,42,0.04)',
    ...Array(16).fill('0 20px 48px rgba(15,23,42,0.12),0 6px 12px rgba(15,23,42,0.04)'),
  ] as unknown as Shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--color-bg-subtle)',
          color: 'var(--color-text-primary)',
          fontSize: 'var(--text-base)',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        },
        '*:focus-visible': {
          outline: '2px solid var(--color-primary)',
          outlineOffset: '2px',
          borderRadius: 4,
        },
        /* ---- react-admin layout shell ----
           react-admin sets `min-width: fit-content` on the layout root, and
           every box below it is a flex item whose default `min-width: auto`
           refuses to shrink below its content. One long string deep inside a
           list therefore stretched the whole document and mobile browsers
           zoomed the page out. Zeroing the minimum lets content ellipsize. */
        '.layout, .RaLayout-appFrame, .RaLayout-contentWithSidebar, .RaLayout-content, .list-page, .RaList-main, .RaList-content':
          {
            minWidth: '0 !important',
            maxWidth: '100%',
          },
        /* Below the tablet breakpoint, clip the content box instead of letting
           an over-wide child (a long e-mail, an untruncated row) push the
           document past the viewport — that would make mobile browsers zoom the
           whole page out. Desktop keeps its natural overflow for wide tables. */
        '@media (max-width: 900px)': {
          '.RaLayout-content': { overflowX: 'hidden' },
          /* react-admin's mobile create FAB defaults to 60px off the bottom,
             which floats it in the middle of the last rows. Pin it to the
             corner; lists reserve bottom padding so nothing hides under it. */
          '.RaCreateButton-floating': {
            bottom: 'var(--space-6) !important',
            right: 'var(--space-4) !important',
            boxShadow: 'var(--shadow-md) !important',
            /* CreateButton renderuje ponizej `md` <Fab color="primary">, ale rozwija
               `{...rest}` PO tym `color` — czyli `sx` wolajacego wygrywa. EmptyBox i
               naglowki list podaja `sx` skrojony pod przycisk tekstowy
               (backgroundColor: transparent, radius-md), co zmienialo FAB-a w
               bezbarwny, kanciasty blob. Wyglad FAB-a wymuszamy wiec centralnie,
               zamiast powtarzac te same `!important` w kazdym wywolaniu. */
            backgroundColor: 'var(--color-primary) !important',
            color: 'var(--color-on-primary) !important',
            borderRadius: 'var(--radius-pill) !important',
            '&:hover': {
              backgroundColor: 'var(--color-primary-hover) !important',
            },
          },
        },
        /* iOS Safari zoomuje strone przy focusie na polu, ktorego font-size
           jest mniejszy niz 16px — i po blurze NIE cofa zoomu. Nasze inputy sa
           z zalozenia 13-14px, wiec na urzadzeniach dotykowych wymuszamy 16px
           na samym renderowanym tekscie pola. `!important` jest konieczne:
           regula `.MuiInputBase-sizeSmall .MuiOutlinedInput-input` z motywu ma
           wyzsza specyficznosc niz selektor elementu. */
        '@media (pointer: coarse)': {
          'input, select, textarea, .MuiInputBase-input, .MuiSelect-select': {
            fontSize: '16px !important',
          },
        },
        /* The AppBar is position:fixed, so we clear it with top padding on the
           content area (this override reliably applies). We do NOT rely on
           appFrame margin-top, which is unreliable here. */
        '.RaLayout-appFrame': {
          marginTop: '0 !important',
        },
        '.RaLayout-content': {
          paddingTop: 'var(--appbar-height) !important',
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          paddingBottom: '0 !important',
          backgroundColor: 'var(--color-bg-subtle)',
        },
        '.RaList-main': {
          backgroundColor: 'transparent',
        },
        '.RaList-content': {
          border: '1px solid var(--color-border) !important',
          borderRadius: 'var(--radius-lg) !important',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
          backgroundColor: 'var(--color-surface)',
        },
        '.RaDatagrid-root': {
          backgroundColor: 'transparent',
        },
        '.RaDatagrid-headerCell': {
          backgroundColor: 'var(--color-bg-subtle) !important',
          fontWeight: '700 !important',
          fontSize: 'var(--text-xs) !important',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary) !important',
          textTransform: 'uppercase',
          borderBottom: '1px solid var(--color-border) !important',
        },
        '.RaDatagrid-rowCell': {
          borderBottom: '1px solid var(--color-hairline) !important',
        },
        '.RaDatagrid-rowEven, .RaDatagrid-rowOdd': {
          backgroundColor: 'transparent !important',
        },
        '.RaDatagrid-tbody .RaDatagrid-row': {
          transition: 'background-color var(--dur-fast) var(--ease-standard)',
          '&:hover': {
            backgroundColor: 'var(--color-primary-soft) !important',
          },
        },
        '.RaDatagrid-tbody tr:last-of-type td': {
          borderBottom: 'none !important',
        },
        '.RaPagination-root': { marginTop: '8px' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.85)',
          color: 'var(--color-text-primary)',
          backdropFilter: 'saturate(180%) blur(12px)',
          WebkitBackdropFilter: 'saturate(180%) blur(12px)',
          borderBottom: '1px solid var(--color-border)',
          boxShadow: 'none',
          '& .MuiTypography-root': { color: 'var(--color-text-primary)' },
          '& .MuiButtonBase-root': { color: 'var(--color-text-secondary)' },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 'var(--appbar-height) !important',
          height: 'var(--appbar-height) !important',
          paddingLeft: 'var(--space-6) !important',
          paddingRight: 'var(--space-6) !important',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 'var(--radius-md)',
          fontWeight: 600,
          paddingInline: 'var(--space-5)',
          paddingBlock: 'var(--space-3)',
          transition:
            'background-color var(--dur-base) var(--ease-standard), box-shadow var(--dur-base) var(--ease-standard), transform var(--dur-fast) var(--ease-standard)',
        },
        containedPrimary: {
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-on-primary)',
          boxShadow: 'var(--shadow-xs)',
          '&:hover': {
            backgroundColor: 'var(--color-primary-hover)',
            boxShadow: 'var(--shadow-md)',
          },
          '&:active': { transform: 'translateY(1px)' },
          '&.Mui-disabled': {
            backgroundColor: 'var(--color-button-background-disabled)',
            color: 'var(--color-button-text-disabled)',
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: 'var(--color-border-strong)',
          color: 'var(--color-text-primary)',
          backgroundColor: 'var(--color-surface)',
          '&:hover': {
            borderColor: 'var(--color-primary-border)',
            backgroundColor: 'var(--color-primary-soft)',
          },
          '&.Mui-disabled': {
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-tertiary)',
          },
        },
        text: {
          color: 'var(--color-text-secondary)',
          '&:hover': {
            backgroundColor: 'var(--color-primary-soft)',
            color: 'var(--color-primary)',
          },
        },
        sizeSmall: { paddingInline: 'var(--space-3)', paddingBlock: 'var(--space-2)' },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '& .MuiTouchRipple-root': { color: 'var(--color-primary)' },
          /* Touch devices need a 44px minimum hit area (WCAG 2.5.5 / HIG).
             Applied to every button-ish base so small icon buttons, dense
             list rows and `size="small"` buttons all comply on a phone.
             The switch thumb is excluded: it is a ButtonBase too, but its
             size is the visual track and growing it breaks the thumb
             geometry — its hit area is widened on the input instead. */
          '@media (pointer: coarse)': {
            '&:not(.MuiSwitch-switchBase)': { minHeight: 44, minWidth: 44 },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (pointer: coarse)': { minHeight: 44, minWidth: 44 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'var(--color-surface)',
        },
        outlined: {
          borderColor: 'var(--color-border)',
          borderRadius: 'var(--radius-lg)',
        },
        elevation1: { boxShadow: 'var(--shadow-sm)' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          backgroundColor: 'var(--color-surface)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid var(--color-hairline)',
          fontSize: 'var(--text-base)',
          color: 'var(--color-text-primary)',
          paddingBlock: 'var(--space-4)',
        },
        head: {
          fontWeight: 700,
        },
        body: {
          color: 'var(--color-text-primary)',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: '1px solid var(--color-hairline)',
          '& .MuiSelect-select': {
            paddingRight: '32px !important',
            paddingLeft: '12px !important',
          },
          '& .MuiSelect-icon': { right: '8px' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-pill)',
          fontWeight: 600,
          fontSize: 'var(--text-xs)',
          height: 24,
          backgroundColor: 'var(--color-bg-muted)',
          color: 'var(--color-text-secondary)',
        },
        sizeSmall: { height: 22, fontSize: 'var(--text-xs)' },
        colorPrimary: {
          backgroundColor: 'var(--color-primary-soft)',
          color: 'var(--color-primary-active)',
          fontWeight: 700,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: 'var(--radius-pill)',
            backgroundColor: 'var(--color-primary)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 'var(--text-base)',
          minHeight: 48,
          color: 'var(--color-text-tertiary)',
          '&.Mui-selected': { color: 'var(--color-primary)' },
          transition: 'color var(--dur-base) var(--ease-standard)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: 'var(--color-border)' } },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--color-surface)',
          transition:
            'box-shadow var(--dur-base) var(--ease-standard), border-color var(--dur-base) var(--ease-standard)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-strong)',
            borderWidth: 1,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-strong)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-primary)',
            borderWidth: 1,
            boxShadow: 'var(--shadow-focus)',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-error)',
          },
          '&.MuiInputBase-sizeSmall .MuiOutlinedInput-input': {
            paddingBlock: '7px',
            fontSize: 'var(--text-sm)',
          },
          /* `size="small"` inputs are 33px tall, which is too small to tap
             comfortably. Font-size na dotyku podnosi globalna regula w
             MuiCssBaseline (`@media (pointer: coarse)`) — tutaj przegrywalaby
             specyficznoscia z reguly `sizeSmall` powyzej. */
          '@media (pointer: coarse)': {
            minHeight: 44,
          },
        },
        input: { fontSize: 'var(--text-base)', paddingBlock: '9px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
          padding: 'var(--space-1)',
          marginTop: 'var(--space-1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-base)',
          minHeight: 40,
          marginBlock: '2px',
          transition: 'background-color var(--dur-fast) var(--ease-standard)',
          '&:hover': { backgroundColor: 'var(--color-primary-soft)' },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-primary-soft)',
            '&:hover': { backgroundColor: 'var(--color-primary-soft-hover)' },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#0f172a',
          fontSize: 'var(--text-xs)',
          borderRadius: 'var(--radius-sm)',
          padding: '6px 10px',
          fontWeight: 500,
          // The tooltip bubble must never sit between the cursor and the
          // button it describes — otherwise its margin area swallows clicks
          // aimed at the top edge of the trigger.
          pointerEvents: 'none',
        },
        popper: {
          // Tighten the default gap so the popper doesn't overlap the trigger.
          '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
            marginBottom: '6px',
          },
          '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
            marginTop: '6px',
          },
        },
        arrow: { color: '#0f172a' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: { root: { color: 'var(--color-primary)' } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-md)',
          transition:
            'background-color var(--dur-fast) var(--ease-standard), color var(--dur-fast) var(--ease-standard)',
        },
      },
    },
    /* react-admin menu items — modern, rounded, with active accent */
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          borderRadius: 'var(--radius-md) !important',
          paddingBlock: '10px !important',
          paddingInline: '12px !important',
          marginBlock: '2px !important',
          color: 'var(--color-text-secondary) !important',
          fontWeight: 600,
          fontSize: 'var(--text-base)',
          transition:
            'background-color var(--dur-fast) var(--ease-standard), color var(--dur-fast) var(--ease-standard) !important',
          '&:hover': {
            backgroundColor: 'var(--color-primary-soft) !important',
            color: 'var(--color-primary) !important',
          },
          '& .RaMenuItemLink-icon': {
            color: 'var(--color-text-muted) !important',
            minWidth: '40px !important',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '40px',
          color: 'var(--color-text-muted)',
          '& svg': { fontSize: '20px' },
        },
      },
    },
  },
});

// helper kept for callers that import alpha (unused now, but harmless)
export const accentAlpha = (a: number) => alpha('#1b8fff', a);
